import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Trash, MagnifyingGlass, Eye, PencilSimple, Printer, PaperPlaneTilt,
  Paperclip, FileText, FolderOpen, ArrowsClockwise,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import ClientDetailSheet from "@/pages/dashboard/ClientDetailSheet";
import ReturnFilesPanel from "@/pages/dashboard/ReturnFilesPanel";

const RETURN_TYPES = ["1040", "1040NR", "1065", "1120", "1120-S", "1041", "FBAR"];
const RETURN_TYPE_BADGE = {
  "1040": "bg-blue-50 text-blue-700 border-blue-200",
  "1040NR": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "1065": "bg-amber-50 text-amber-700 border-amber-200",
  "1120": "bg-rose-50 text-rose-700 border-rose-200",
  "1120-S": "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  "1041": "bg-orange-50 text-orange-700 border-orange-200",
  "FBAR": "bg-slate-100 text-slate-700 border-slate-200",
};

const STATUSES = [
  { value: "preparing", label: "Preparing", tone: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "in_review", label: "In Review", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "waiting_client", label: "Waiting for Client", tone: "bg-orange-50 text-orange-700 border-orange-200" },
  { value: "ready_signature", label: "Ready for Signature", tone: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" },
  { value: "filed", label: "Filed", tone: "bg-sky-50 text-sky-700 border-sky-200" },
  { value: "accepted", label: "Accepted", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "rejected", label: "Rejected", tone: "bg-rose-50 text-rose-700 border-rose-200" },
  { value: "archived", label: "Archived", tone: "bg-slate-100 text-slate-700 border-slate-200" },
];
const STATUS_META = Object.fromEntries(STATUSES.map(s => [s.value, s]));

const FILING_STATUSES = [
  { value: "single", label: "Single" },
  { value: "mfj", label: "MFJ" },
  { value: "mfs", label: "MFS" },
  { value: "hoh", label: "HOH" },
  { value: "qss", label: "QSS" },
  { value: "partnership", label: "Partnership" },
  { value: "s_corp", label: "S-Corp" },
  { value: "c_corp", label: "C-Corp" },
  { value: "trust", label: "Trust / Estate" },
];

const PAYMENT_STATUSES = [
  { value: "unpaid",   label: "Unpaid",    tone: "bg-rose-50 text-rose-700 border-rose-200" },
  { value: "pending",  label: "Pending",   tone: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "paid",     label: "Paid",      tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "refunded", label: "Refunded",  tone: "bg-sky-50 text-sky-700 border-sky-200" },
  { value: "waived",   label: "Waived",    tone: "bg-slate-100 text-slate-700 border-slate-200" },
];
const PAY_STATUS_META = Object.fromEntries(PAYMENT_STATUSES.map(s => [s.value, s]));

const emptyForm = {
  tax_year: new Date().getFullYear() - 1,
  return_type: "1040",
  status: "preparing",
  filing_status: "single",
  client_name: "",
  assigned_to_id: "",
  assigned_to_name: "",
  refund_amount: 0,
  tax_liability: 0,
  balance_due: 0,
  fee: 0,
  payment_status: "unpaid",
  due_date: "",
  progress: 0,
  notes: "",
};

const PAGE_SIZE = 25;

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, tone: "bg-slate-100 text-slate-700 border-slate-200" };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${meta.tone}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${meta.tone.split(" ").find(c => c.startsWith("text-"))?.replace("text-", "bg-") || "bg-slate-500"}`} />
      {meta.label}
    </span>
  );
}

function TypeBadge({ type }) {
  const tone = RETURN_TYPE_BADGE[type] || "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`inline-block text-xs font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${tone}`}>{type}</span>;
}

function PaymentBadge({ status }) {
  const meta = PAY_STATUS_META[status] || { label: status || "—", tone: "bg-slate-100 text-slate-700 border-slate-200" };
  return (
    <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${meta.tone}`}>
      {meta.label}
    </span>
  );
}

function ProgressBar({ value }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div className="h-full bg-[#0A2540]" style={{ width: `${v}%` }} />
      </div>
      <span className="text-xs font-mono text-slate-600 tabular-nums w-9 text-right">{v}%</span>
    </div>
  );
}

export default function Returns() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.role && user.role !== "client";
  const canDelete = user?.role === "admin" || user?.role === "cpa";
  const canEfile = user?.role === "admin" || user?.role === "cpa";
  const canEditClient = ["admin", "cpa", "document_team"].includes(user?.role);

  const [returns, setReturns] = useState([]);
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [filterYear, setFilterYear] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [page, setPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [toEfile, setToEfile] = useState(null);
  const [clientSheet, setClientSheet] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const { data } = await api.get("/returns");
    setReturns(data);
  }, []);

  const loadStaff = useCallback(async () => {
    if (!isStaff) return;
    try {
      const { data } = await api.get("/employees");
      setStaff(data);
    } catch (e) {
      // 403 = staff-only endpoint viewed by client — expected and silent.
      // Surface anything else so we don't hide real breakage.
      if (e?.response?.status && e.response.status !== 403) {
        console.warn("/employees load failed:", e.response.status, e.response.data);
      }
    }
  }, [isStaff]);

  useEffect(() => { load(); loadStaff(); }, [load, loadStaff]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return returns.filter(r => {
      if (needle && !(
        (r.client_name || r.owner_name || "").toLowerCase().includes(needle) ||
        (r.return_type || "").toLowerCase().includes(needle) ||
        (r.notes || "").toLowerCase().includes(needle) ||
        String(r.tax_year || "").includes(needle)
      )) return false;
      if (filterYear !== "all" && String(r.tax_year) !== filterYear) return false;
      if (filterType !== "all" && r.return_type !== filterType) return false;
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (filterAssignee !== "all" && (r.assigned_to_id || "") !== filterAssignee) return false;
      return true;
    });
  }, [returns, q, filterYear, filterType, filterStatus, filterAssignee]);

  const years = useMemo(() => Array.from(new Set(returns.map(r => r.tax_year))).sort((a, b) => b - a), [returns]);

  const stats = useMemo(() => {
    const s = { total: returns.length, preparing: 0, review: 0, waiting: 0, accepted: 0, rejected: 0, extensions: 0 };
    for (const r of returns) {
      if (r.status === "preparing") s.preparing++;
      if (r.status === "in_review") s.review++;
      if (r.status === "waiting_client") s.waiting++;
      if (r.status === "accepted") s.accepted++;
      if (r.status === "rejected") s.rejected++;
      if (r.return_type === "Extension") s.extensions++;
    }
    return s;
  }, [returns]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSlice = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [q, filterYear, filterType, filterStatus, filterAssignee]);

  const resetFilters = () => {
    setQ(""); setFilterYear("all"); setFilterType("all"); setFilterStatus("all"); setFilterAssignee("all");
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, client_name: isStaff ? "" : (user?.name || "") });
    setFormOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...emptyForm, ...r });
    setFormOpen(true);
  };

  const submit = async () => {
    const payload = {
      ...form,
      tax_year: Number(form.tax_year),
      refund_amount: Number(form.refund_amount || 0),
      tax_liability: Number(form.tax_liability || 0),
      balance_due: Number(form.balance_due || 0),
      fee: Number(form.fee || 0),
      progress: Number(form.progress || 0),
    };
    try {
      if (editing) {
        await api.patch(`/returns/${editing.id}`, payload);
        toast.success("Return updated");
      } else {
        await api.post("/returns", payload);
        toast.success("Tax return created");
      }
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/returns/${toDelete.id}`);
      toast.success("Return deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const confirmEfile = async () => {
    if (!toEfile) return;
    try {
      await api.post(`/returns/${toEfile.id}/file`);
      toast.success(`Return e-filed to IRS. Confirmation will follow.`);
      setToEfile(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "E-file failed");
    }
  };

  const goDocs = (r) => {
    navigate(`/dashboard/documents?return=${r.id}&client=${encodeURIComponent(r.owner_name || "")}`);
  };

  const printReturn = (r) => {
    window.open(`/dashboard/returns/${r.id}/print`, "_blank", "noopener");
  };

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="returns-title">Tax Returns</h1>
          <p className="text-slate-600 mt-1">Every filing — from intake to IRS acceptance.</p>
        </div>
        {user?.role !== "client" && (
          <Button className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" onClick={openCreate} data-testid="create-return-btn" disabled={!canEditClient} title={canEditClient ? undefined : "Only admin, CPA or document team can create returns."}>
            <Plus size={16} className="mr-1.5" /> Create Tax Return
          </Button>
        )}
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { label: "Total Returns", value: stats.total, tone: "text-[#0A2540]" },
          { label: "Preparing", value: stats.preparing, tone: "text-blue-700" },
          { label: "Waiting Client", value: stats.waiting, tone: "text-orange-700" },
          { label: "Accepted", value: stats.accepted, tone: "text-emerald-700" },
          { label: "Rejected", value: stats.rejected, tone: "text-rose-700" },
          { label: "Extensions", value: stats.extensions, tone: "text-fuchsia-700" },
        ].map(k => (
          <div key={k.label} className="rounded-md border tf-hairline bg-white p-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{k.label}</div>
            <div className={`mt-1.5 font-[Chivo] text-2xl font-black tabular-nums ${k.tone}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* FILTERS */}
      <div className="rounded-md border tf-hairline bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, type, notes…" className="pl-9" data-testid="returns-search" />
          </div>
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-32" data-testid="filter-year"><SelectValue placeholder="Tax Year" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All years</SelectItem>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-32" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem>{RETURN_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          {isStaff && (
            <Select value={filterAssignee} onValueChange={setFilterAssignee}>
              <SelectTrigger className="w-44" data-testid="filter-assignee"><SelectValue placeholder="Assigned Staff" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All staff</SelectItem>{staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="reset-filters"><ArrowsClockwise size={13} className="mr-1" /> Reset</Button>
        </div>
      </div>

      {/* TABLE */}
      <div className="rounded-md border tf-hairline bg-white overflow-x-auto tf-scroll">
        <table className="w-full text-sm" data-testid="returns-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left p-3">Year</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Client</th>
              <th className="text-left p-3">Filing</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Progress</th>
              <th className="text-left p-3">Assigned</th>
              <th className="text-right p-3">Refund</th>
              <th className="text-right p-3">Balance</th>
              <th className="text-right p-3">Fee</th>
              <th className="text-left p-3">Pay</th>
              <th className="text-left p-3">Due</th>
              <th className="text-left p-3">Updated</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={14} className="py-16">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <FolderOpen size={40} weight="duotone" className="text-slate-400" />
                    <div className="font-[Chivo] font-bold text-[#0A2540] text-lg">No Tax Returns Found</div>
                    <p className="text-sm text-slate-500 max-w-sm">Start by creating your first tax return, or reset the filters if you're looking for a specific one.</p>
                    {canEditClient && (
                      <Button className="bg-[#D32F2F] hover:bg-[#D32F2F]/90 mt-2" onClick={openCreate} data-testid="empty-create-btn">
                        <Plus size={16} className="mr-1.5" /> Create Tax Return
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {pageSlice.map(r => (
              <tr key={r.id} className="border-t tf-hairline hover:bg-slate-50/70">
                <td className="p-3 font-mono">{r.tax_year}</td>
                <td className="p-3"><TypeBadge type={r.return_type} /></td>
                <td className="p-3 text-[#0A2540] font-semibold">
                  {isStaff ? (
                    <button
                      type="button"
                      onClick={() => setClientSheet(r)}
                      className="text-left hover:text-[#D32F2F] hover:underline decoration-dotted underline-offset-4 transition-colors"
                      data-testid={`open-client-${r.id}`}
                    >
                      {r.client_name || r.owner_name}
                    </button>
                  ) : (
                    r.client_name || r.owner_name
                  )}
                </td>
                <td className="p-3 text-xs font-mono uppercase tracking-widest text-slate-600">{r.filing_status || "—"}</td>
                <td className="p-3"><StatusBadge status={r.status} /></td>
                <td className="p-3"><ProgressBar value={r.progress} /></td>
                <td className="p-3 text-slate-600 text-xs">{r.assigned_to_name || "—"}</td>
                <td className="p-3 text-right font-mono text-emerald-700 tabular-nums">{r.refund_amount ? `$${Number(r.refund_amount).toLocaleString()}` : "—"}</td>
                <td className="p-3 text-right font-mono text-rose-700 tabular-nums">{r.balance_due ? `$${Number(r.balance_due).toLocaleString()}` : "—"}</td>
                <td className="p-3 text-right font-mono tabular-nums text-[#0A2540]">{r.fee ? `$${Number(r.fee).toLocaleString()}` : "—"}</td>
                <td className="p-3"><PaymentBadge status={r.payment_status || "unpaid"} /></td>
                <td className="p-3 font-mono text-xs">{r.due_date || "—"}</td>
                <td className="p-3 font-mono text-xs text-slate-500 whitespace-nowrap">{(r.updated_at || r.created_at || "").split("T")[0]}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-0.5">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="View" onClick={() => setViewing(r)} data-testid={`view-${r.id}`}><Eye size={14} className="text-[#0A2540]" /></Button>
                    {canEditClient && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Edit" onClick={() => openEdit(r)} data-testid={`edit-${r.id}`}><PencilSimple size={14} className="text-[#0A2540]" /></Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Print" onClick={() => printReturn(r)} data-testid={`print-${r.id}`}><Printer size={14} className="text-[#0A2540]" /></Button>
                    {canEfile && r.status !== "filed" && r.status !== "accepted" && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="E-file" onClick={() => setToEfile(r)} data-testid={`efile-${r.id}`}><PaperPlaneTilt size={14} className="text-sky-600" /></Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Upload Documents" onClick={() => goDocs(r)} data-testid={`upload-${r.id}`}><Paperclip size={14} className="text-[#0A2540]" /></Button>
                    {canDelete && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Delete" onClick={() => setToDelete(r)} data-testid={`delete-return-${r.id}`}><Trash size={14} className="text-[#D32F2F]" /></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* FOOTER */}
      {filtered.length > 0 && (
        <div className="flex items-center justify-between text-xs text-slate-600">
          <div data-testid="returns-footer">
            Showing <strong>{(page - 1) * PAGE_SIZE + 1}</strong>–<strong>{Math.min(page * PAGE_SIZE, filtered.length)}</strong> of <strong>{filtered.length}</strong> returns
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="page-prev">Previous</Button>
            <span className="font-mono">Page {page} / {pageCount}</span>
            <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))} data-testid="page-next">Next</Button>
          </div>
        </div>
      )}

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit tax return" : "Create Tax Return"}</DialogTitle>
            <DialogDescription>{editing ? "Update the return details below." : "Enter the core information — you can update it as you progress."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tax Year</Label><Input type="number" value={form.tax_year} onChange={(e) => setForm({ ...form, tax_year: e.target.value })} data-testid="form-year" /></div>
              <div>
                <Label>Return Type</Label>
                <Select value={form.return_type} onValueChange={(v) => setForm({ ...form, return_type: v })}>
                  <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{RETURN_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Client Name</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="form-client" /></div>
              <div>
                <Label>Filing Status</Label>
                <Select value={form.filing_status} onValueChange={(v) => setForm({ ...form, filing_status: v })}>
                  <SelectTrigger data-testid="form-filing"><SelectValue /></SelectTrigger>
                  <SelectContent>{FILING_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {isStaff && (
                <div>
                  <Label>Assigned Staff</Label>
                  <Select
                    value={form.assigned_to_id || "unassigned"}
                    onValueChange={(v) => {
                      const s = staff.find((x) => x.id === v);
                      setForm({ ...form, assigned_to_id: s ? s.id : "", assigned_to_name: s ? s.name : "" });
                    }}
                  >
                    <SelectTrigger data-testid="form-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Refund ($)</Label><Input type="number" step="0.01" value={form.refund_amount} onChange={(e) => setForm({ ...form, refund_amount: e.target.value })} /></div>
              <div><Label>Balance Due ($)</Label><Input type="number" step="0.01" value={form.balance_due} onChange={(e) => setForm({ ...form, balance_due: e.target.value })} /></div>
              <div><Label>Tax Liability ($)</Label><Input type="number" step="0.01" value={form.tax_liability} onChange={(e) => setForm({ ...form, tax_liability: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Preparation Fee ($)</Label>
                <Input type="number" step="0.01" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} data-testid="form-fee" />
              </div>
              <div>
                <Label>Payment Status</Label>
                <Select value={form.payment_status || "unpaid"} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                  <SelectTrigger data-testid="form-payment-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div><Label>Progress (%)</Label><Input type="number" min={0} max={100} value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button className="bg-[#0A2540]" onClick={submit} data-testid="form-submit">{editing ? "Save changes" : "Create return"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIEW DIALOG */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-3"><FileText size={20} /> Return details</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-3">
                <TypeBadge type={viewing.return_type} />
                <StatusBadge status={viewing.status} />
                <span className="font-mono text-xs text-slate-500 ml-auto">{viewing.tax_year}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <div><div className="text-[10px] uppercase font-mono text-slate-500">Client</div><div>{viewing.client_name || viewing.owner_name}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-slate-500">Filing</div><div>{viewing.filing_status || "—"}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-slate-500">Assigned</div><div>{viewing.assigned_to_name || "Unassigned"}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-slate-500">Due date</div><div>{viewing.due_date || "—"}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-slate-500">Refund</div><div className="text-emerald-700 font-mono">${Number(viewing.refund_amount || 0).toLocaleString()}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-slate-500">Balance due</div><div className="text-rose-700 font-mono">${Number(viewing.balance_due || 0).toLocaleString()}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-slate-500">Tax liability</div><div className="font-mono">${Number(viewing.tax_liability || 0).toLocaleString()}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-slate-500">Preparation fee</div><div className="font-mono">${Number(viewing.fee || 0).toLocaleString()}</div></div>
                <div><div className="text-[10px] uppercase font-mono text-slate-500">Payment status</div><PaymentBadge status={viewing.payment_status || "unpaid"} /></div>
                <div><div className="text-[10px] uppercase font-mono text-slate-500">Progress</div><ProgressBar value={viewing.progress} /></div>
              </div>
              {viewing.notes && (
                <div><div className="text-[10px] uppercase font-mono text-slate-500 mb-1">Notes</div><div className="rounded bg-slate-50 border tf-hairline p-3">{viewing.notes}</div></div>
              )}
              <ReturnFilesPanel returnRow={viewing} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
            {viewing && <Button className="bg-[#0A2540]" onClick={() => { openEdit(viewing); setViewing(null); }}><PencilSimple size={14} className="mr-1.5" />Edit</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tax return?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && `${toDelete.return_type} · Tax year ${toDelete.tax_year} · ${toDelete.client_name || toDelete.owner_name}`}. This permanently removes the record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-return-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="delete-return-confirm">Delete return</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* E-FILE CONFIRM */}
      <AlertDialog open={!!toEfile} onOpenChange={(v) => !v && setToEfile(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit this return to the IRS?</AlertDialogTitle>
            <AlertDialogDescription>
              {toEfile && `${toEfile.return_type} · ${toEfile.tax_year} · ${toEfile.client_name || toEfile.owner_name}`}. This marks the return as filed and stamps the submission timestamp. You'll receive IRS acknowledgement separately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="efile-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmEfile} className="bg-sky-600 hover:bg-sky-700" data-testid="efile-confirm">
              <PaperPlaneTilt size={14} className="mr-1.5" /> E-file now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CLIENT DETAIL SHEET (staff-only via clickable client name) */}
      <ClientDetailSheet
        open={!!clientSheet}
        onOpenChange={(v) => !v && setClientSheet(null)}
        client={clientSheet}
      />
    </div>
  );
}
